import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, from, of, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { ENC_HEADER, ENVELOPE_FIELD, PayloadCipher } from './payload-cipher';

/**
 * Encrypts outgoing request bodies and decrypts incoming responses, so payloads
 * do not appear in the browser's Network tab, in proxies, or in traffic logs.
 *
 * Controlled entirely by `environment.encryption` — see `environment.ts`.
 * Setting `enabled: false` restores plaintext with no backend change, because
 * the server only decrypts requests that carry the `X-Enc` header and mirrors
 * whatever the client did. That asymmetry is deliberate: it makes the flag a
 * one-sided switch and lets un-updated clients (the Capacitor build, curl,
 * Swagger, the admin API console) keep working against the same server.
 *
 * ⚠️ See the note in `payload-cipher.ts` — this hides payloads from casual
 * inspection and from anything sitting on the wire, not from a user determined
 * to read them in DevTools. The backend's `@PreAuthorize` checks remain the
 * only real access boundary.
 */
@Injectable()
export class PayloadEncryptionInterceptor implements HttpInterceptor {
  private readonly cipher = new PayloadCipher(environment.encryption?.key ?? '');

  /**
   * Active only when switched on, a valid key is present, and SubtleCrypto
   * exists (it is absent on insecure origins). Any of those failing degrades to
   * plaintext rather than breaking every request.
   */
  private readonly active =
    !!environment.encryption?.enabled && this.cipher.isConfigured();

  constructor() {
    if (environment.encryption?.enabled && !this.active) {
      // Loud, because the intent was to encrypt and it silently is not.
      console.warn(
        '[payload-encryption] enabled but inactive — check that encryption.key is 32 bytes of base64 and the page is on a secure origin (https or localhost).',
      );
    }
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!this.active || !this.shouldEncrypt(req)) {
      return next.handle(req);
    }

    const plaintext = JSON.stringify(req.body);

    return from(this.cipher.encrypt(plaintext)).pipe(
      switchMap((sealed) => {
        const encrypted = req.clone({
          body: { [ENVELOPE_FIELD]: sealed },
          setHeaders: { [ENC_HEADER]: '1', 'Content-Type': 'application/json' },
        });
        return next.handle(encrypted).pipe(
          switchMap((event) => this.decryptResponse(event)),
          // Angular routes 4xx/5xx down the error channel as HttpErrorResponse,
          // which never passes through the success operator above. Without this
          // the backend's error bodies stay sealed and every failure surfaces to
          // the user as a blank message.
          catchError((error) => this.decryptError(error)),
        );
      }),
    );
  }

  /**
   * Bodies we must leave alone:
   *  - no body at all (GET/DELETE) — nothing to seal, and the response is still
   *    decrypted because the request carries no X-Enc header either, so the
   *    server replies in plaintext by design;
   *  - FormData: image and CSV uploads stream as multipart, and JSON-stringify
   *    would destroy them;
   *  - blob/arraybuffer responses: report exports are binary downloads.
   */
  private shouldEncrypt(req: HttpRequest<unknown>): boolean {
    if (req.body === null || req.body === undefined) {
      return false;
    }
    if (typeof FormData !== 'undefined' && req.body instanceof FormData) {
      return false;
    }
    return req.responseType === 'json';
  }

  private decryptResponse(
    event: HttpEvent<unknown>,
  ): Observable<HttpEvent<unknown>> {
    if (!(event instanceof HttpResponse)) {
      return of(event);
    }
    const body = event.body as Record<string, unknown> | null;
    const sealed = body?.[ENVELOPE_FIELD];
    if (typeof sealed !== 'string') {
      // Server replied in plaintext (an endpoint that opted out, or an error
      // raised before the filter sealed anything). Pass it straight through.
      return of(event);
    }
    return from(this.cipher.decrypt(sealed)).pipe(
      switchMap((plaintext) =>
        of(event.clone({ body: plaintext ? JSON.parse(plaintext) : null })),
      ),
    );
  }

  /**
   * Unseal an error body and re-throw the error with readable contents, so
   * downstream handlers (and the toasts they raise) see the backend's actual
   * message rather than an opaque envelope.
   *
   * A rebuilt HttpErrorResponse is required because `error` is readonly. If the
   * payload cannot be decrypted the original error is re-thrown untouched —
   * losing the real failure behind a decryption failure would be worse than an
   * unreadable body. Note the filter deliberately sends its own crypto errors in
   * plaintext, so those land in the `typeof !== 'string'` path below.
   */
  private decryptError(error: unknown): Observable<never> {
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }
    const sealed = (error.error as Record<string, unknown> | null)?.[
      ENVELOPE_FIELD
    ];
    if (typeof sealed !== 'string') {
      return throwError(() => error);
    }
    return from(this.cipher.decrypt(sealed)).pipe(
      // The guard belongs HERE, on the decrypt itself. Downstream of the
      // switchMap it would also catch the deliberate throw below and swap the
      // decrypted error back for the sealed one — silently undoing the whole
      // point of this method.
      catchError(() => of(null)),
      switchMap((plaintext) =>
        throwError(() =>
          plaintext === null
            ? error
            : new HttpErrorResponse({
                error: plaintext ? JSON.parse(plaintext) : null,
                headers: error.headers,
                status: error.status,
                statusText: error.statusText,
                url: error.url ?? undefined,
              }),
        ),
      ),
    );
  }
}
